import { Alert } from 'bold-ui'
import {
  Chart,
  ChartBody,
  ChartContainer,
  ChartFooter,
  DataPoint,
  DateRange,
  getDomainPoints,
  ReferenceArea,
  SeriesType,
  ValueRange,
} from 'bold-ui/lib/components/Chart'
import { blue, gray, green, red, yellow } from 'bold-ui/lib/styles/colors'
import { SexoEnum } from 'graphql/types.generated'
import moment, { Duration, Moment } from 'moment'
import { calcularIMC, ImcRange, ParametrosImcAdulto, ParametrosImcIdosoEGestante } from 'util/atendimento'
import { calculateIdadeGestacionalParaGraficos } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { filterGestacoesPeriodo } from 'util/atendimento/gestante/filterGestacoesPeriodo'
import imcAdulto from 'util/atendimento/parametros/imcAdulto.json'
import imcIdoso from 'util/atendimento/parametros/imcIdoso.json'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { PeriodoGestacaoModel } from '../../../types/PeriodoGestacaoModel'
import { MedicaoAntropometricaPreNatal } from '../../pre-natal/model-medicoesPreNatal'
import {
  getEndGestacao,
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatal,
} from '../../pre-natal/util-medicoesPreNatal'
import { reduceMedicoesIguais } from '../../util-medicoes'
import { LinhaTooltipGrafico } from '../components/LinhaTooltipGrafico'
import { getReferenceAreasCalculators, ReferenceAreasCalculator } from './referenceAreaCalculators'
import { TickIdade } from './TickIdade'
import { buildAreasPreTermo, PeriodoPreTermo } from './util-grafico-pre-termo'

export interface GraficoImcProps {
  medicoes: ReadonlyArray<HistoricoMedicaoModel>
  dataNascimento: Moment
  dataRange: DateRange
  sexo: SexoEnum
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isGestante: boolean
  periodoPreTermo?: Readonly<PeriodoPreTermo>
  shouldRenderPreTermo?: boolean
}

type ReferenceAreaStrategy = (
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment
) => ReferenceArea<Moment>[]

type TooltipRendererPoint<XDomain> = DataPoint<XDomain> & {
  seriesName: string
}

// TODO@CODEBENDERS - Adicionar funcionalidade de outliers #23674
export function GraficoImcView(props: Readonly<GraficoImcProps>) {
  // TODO@CODEBENDERS - Colocar componente dentro do escopo do AtendimentoProvider #25821
  const {
    medicoes,
    dataNascimento,
    dataRange,
    sexo,
    gestacoes,
    isGestante,
    periodoPreTermo,
    shouldRenderPreTermo,
  } = props

  const maxIdadeNoRange = moment.duration(moment(dataRange.end).diff(dataNascimento))
  const medicoesComImc = reduceMedicoesIguais(medicoes, 'valorImc') ?? []
  const seriesData = medicoesComImc.map((m) => ({
    x: m.dataMedicao,
    y: m.valorImc,
  }))
  const yRange = getYRange(maxIdadeNoRange, isGestante)
  const gestacoesPeriodo = filterGestacoesPeriodo(gestacoes, moment(dataRange.init), moment(dataRange.end))
  const getAreas = resolveReferenceAreaStrategy(maxIdadeNoRange, isGestante)
  const referenceAreas = getAreas(sexo, dataRange, maxIdadeNoRange, dataNascimento)
  const getImcReferenciaMedicao = (inicioGestacao: Date): MedicaoAntropometricaPreNatal => {
    const imcReferencia = getMedicaoForImcReferencia(inicioGestacao, getMedicoesAntropometricasPreNatal(medicoes))
    if (!imcReferencia) return
    return imcReferencia
  }

  const getImcReferenciaValue = (inicioGestacao: Date): number => {
    const imcReferencia = getImcReferenciaMedicao(inicioGestacao)
    if (!imcReferencia) return null
    return imcReferencia && calcularIMC(imcReferencia.peso, imcReferencia.altura)
  }
  const seriesDataWithoutGestacaoEnd = seriesData.filter(
    (point) => !gestacoesPeriodo.some((g) => point.x === new Date(g.fim).getTime())
  )

  const seriesDataWithoutPlotsInImcReferencia = seriesDataWithoutGestacaoEnd.filter(
    (point) =>
      !gestacoesPeriodo.some((gestacao) => {
        const inicioGestacao = moment(gestacao.inicio).startOf('day')
        const inicioOffSetGestacao = moment(inicioGestacao).subtract(2, 'month')
        const endOffSetGestacao = moment(inicioGestacao).add(8, 'week')
        const imcReferencia = getImcReferenciaMedicao(inicioGestacao.toDate())
        if (!imcReferencia) return false

        return (
          point.x >= inicioOffSetGestacao.toDate().getTime() &&
          point.x <= endOffSetGestacao.toDate().getTime() &&
          !moment(point.x).isSame(moment(imcReferencia.dataMedicao), 'day')
        )
      })
  )

  const imcReferenciaGestacoesData = gestacoes
    .map((g) => {
      const inicioGestacao = moment(g.inicio).startOf('day')
      const imcReferenciaValue = getImcReferenciaMedicao(inicioGestacao.toDate())
      if (!imcReferenciaValue) return null
      return moment(imcReferenciaValue.dataMedicao)
    })
    .filterNotNull()

  function renderTooltip<XDomain>(
    points: TooltipRendererPoint<XDomain>[],
    gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  ) {
    if (!points || !points.length) return
    const momentX = moment(points[0].x)
    const { idadeGestacional, hasGestacaoPeriodo } = calculateIdadeGestacionalParaGraficos(gestacoes, momentX)

    return (
      <>
        {points?.map((p) => (
          <LinhaTooltipGrafico key={p.seriesName}>
            {`IMC${imcReferenciaGestacoesData.some((g) => g.isSame(momentX, 'day')) ? ' pré-gestacional' : ''}: ${
              p.y
            } kg/m²`}
          </LinhaTooltipGrafico>
        ))}
        {hasGestacaoPeriodo && (
          <LinhaTooltipGrafico>
            {`IG: ${idadeGestacional} ${idadeGestacional === 1 ? 'semana' : 'semanas'}`}
          </LinhaTooltipGrafico>
        )}
        <LinhaTooltipGrafico>{`Data: ${momentX.format('DD/MM/YYYY')}`}</LinhaTooltipGrafico>
      </>
    )
  }

  const renderRangeAreas = (gestacoes: ReadonlyArray<PeriodoGestacaoModel>) => {
    const areasGestacao = gestacoes.map((g) => {
      const inicioGestacao = moment(g.inicio).startOf('day')
      const fimGestacao = getEndGestacao(g)

      const inicioHideDotsGestacao = moment(g.inicio).startOf('day').add(8, 'week')

      const inicioDataRange = moment(dataRange.init).startOf('day').add(1, 'day')
      const fimDataRange = moment(dataRange.end).startOf('day')

      const inicioMask = inicioGestacao.isBefore(inicioDataRange) ? inicioDataRange : inicioGestacao
      const fimMask = fimGestacao.isAfter(fimDataRange) ? fimDataRange : fimGestacao

      const imcReferenciaValue = getImcReferenciaValue(inicioGestacao.toDate())

      return {
        name: 'Gestação',
        init: inicioMask,
        end: fimMask,
        fillColor: 'none',
        tickColor: blue.c60,
        strokeColor: gray.c40,
        tooltip: 'Somente o IMC pré-gestacional é considerado no período da gestação',
        mask: {
          show: true,
          hideDots: {
            from: inicioHideDotsGestacao,
            to: fimMask,
          },
          fillOpacity: 1,
          yAtEnd: imcReferenciaValue,
        },
      }
    })

    const areasPreTermo = !shouldRenderPreTermo && periodoPreTermo ? buildAreasPreTermo(periodoPreTermo, dataRange) : []

    return [...areasGestacao, ...areasPreTermo]
  }

  return (
    <div>
      {isGestante && (
        <Alert type='info' styles={{ container: styles.iconAlertImcGestante }} style={styles.alertImcGestante}>
          O IMC não é recomendado como indicador durante a gestação. Utilize o gráfico de ganho de peso para
          monitoramento adequado.
        </Alert>
      )}
      <ChartContainer>
        <ChartBody height={500}>
          <Chart<Moment>
            outliers='auto'
            type={SeriesType.Area}
            series={[{ name: 'IMC', data: seriesDataWithoutPlotsInImcReferencia }]}
            referenceAreas={referenceAreas}
            xAxis={{
              title: 'Idade (meses completos e anos)',
              domain: dataRange,
              tickRenderer: (props) => <TickIdade {...props} dataNascimento={dataNascimento} />,
            }}
            rangeAreas={renderRangeAreas(gestacoesPeriodo)}
            yAxis={{ title: 'IMC', unit: 'kg/m²', domain: yRange }}
            showLegend={false}
            tooltip={{
              type: 'point',
              render: (points) => renderTooltip(points, gestacoesPeriodo),
            }}
          />
        </ChartBody>
        <ChartFooter>
          {getFooterText(sexo, maxIdadeNoRange, dataNascimento, gestacoes, isGestante, !!gestacoesPeriodo.length)}
        </ChartFooter>
      </ChartContainer>
    </div>
  )
}

function getFooterText(
  sexo: SexoEnum,
  maxIdadeNoRange: Duration,
  dataNascimento: Moment,
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>,
  isGestante: boolean,
  hasGestacaoPeriodo: boolean
): string {
  const idadeMax = maxIdadeNoRange.years()
  const isMasculino = sexo === SexoEnum.MASCULINO
  const isFeminino = sexo === SexoEnum.FEMININO
  const complementoGestante = isGestante && hasGestacaoPeriodo ? ' e Caderneta da Gestante - 2018' : ''
  const hasGestacaoNaFaseAdulta =
    isGestante && gestacoes.some((g) => moment(g.inicio).diff(dataNascimento, 'years') > 19)

  const faixaCaderneta = (masc: string, fem: string) => {
    if (isMasculino) return masc
    if (isFeminino) return `${fem}${complementoGestante}.`
    return ''
  }

  if (idadeMax <= 10) {
    return faixaCaderneta(
      'Fonte: Caderneta de Saúde da Criança Menino - 2020.',
      'Fonte: Caderneta de Saúde da Criança Menina - 2020'
    )
  }

  if (idadeMax <= 19) {
    return faixaCaderneta(
      'Fonte: Caderneta de Saúde do Adolescente - 2013.',
      'Fonte: Caderneta de Saúde da Adolescente - 2013'
    )
  }

  if (hasGestacaoNaFaseAdulta) {
    return 'Fonte: Caderneta da Gestante - 2018.'
  }

  return 'Fonte: Ministério da Saúde - 2022.'
}

function getYRange(maxIdadeNoRange: Duration, isGestante: boolean): ValueRange {
  if (maxIdadeNoRange.years() <= 2) return { init: 5, end: 25, step: 1 }
  else if (maxIdadeNoRange.years() <= 5) return { init: 10, end: 25, step: 1 }
  else if (maxIdadeNoRange.years() <= 10) return { init: 10, end: 30, step: 1 }
  else if (maxIdadeNoRange.years() <= 19 || isGestante) return { init: 10, end: 40, step: 2 }
  else return { init: 10, end: 46, step: 2 }
}

function resolveReferenceAreaStrategy(idadeNoRange: Duration, isGestante: boolean): ReferenceAreaStrategy {
  return isGestante && idadeNoRange.years() > 19 ? getReferenceAreasForGestante : getReferenceAreasForNaoGestante
}

function getReferenceAreasForGestante(
  _sexo: SexoEnum,
  xRange: DateRange,
  _idadeNoRange: Duration,
  _dataNascimento: Moment
): ReferenceArea<Moment>[] {
  return [
    getReferenceAreaAdultoBaixoPeso(xRange),
    getReferenceAreaAdultoAdequado(xRange),
    getReferenceAreaAdultoSobrepeso(xRange),
    getReferenceAreaAdultoObesidade(xRange),
  ]
}

function getReferenceAreasForNaoGestante(
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment
): ReferenceArea<Moment>[] {
  if (idadeNoRange.years() <= 19) {
    const refAreaCalculators = getReferenceAreasCalculators(sexo, dataNascimento, xRange, 'imc')
    return [
      getReferenceAreaCriancaMagrezaAcentuada(refAreaCalculators),
      getReferenceAreaCriancaMagreza(refAreaCalculators),
      getReferenceAreaCriancaEutrofia(refAreaCalculators),
      getReferenceAreaCriancaSobrepeso(refAreaCalculators, idadeNoRange),
      getReferenceAreaCriancaObesidade(refAreaCalculators, idadeNoRange),
      getReferenceAreaCriancaObesidadeGrave(refAreaCalculators, idadeNoRange),
    ]
  } else if (idadeNoRange.years() <= 60) {
    return [
      getReferenceAreaAdultoBaixoPeso(xRange),
      getReferenceAreaAdultoAdequado(xRange),
      getReferenceAreaAdultoSobrepeso(xRange),
      getReferenceAreaAdultoObesidade1(xRange),
      getReferenceAreaAdultoObesidade2(xRange),
      getReferenceAreaAdultoObesidade3(xRange),
    ]
  } else {
    return [
      getReferenceAreaIdosoBaixoPeso(xRange),
      getReferenceAreaIdosoAdequado(xRange),
      getReferenceAreaIdosoSobrepeso(xRange),
    ]
  }
}

function getReferenceAreaIdosoBaixoPeso(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitIdoso('baixo'),
    })),
    label: {
      name: 'Baixo peso',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaIdosoAdequado(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitIdoso('adequado'),
    })),
    label: {
      name: 'Adequado ou Eutrófico',
    },
    color: green.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaIdosoSobrepeso(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitIdoso('sobrepeso'),
    })),
    label: {
      name: 'Sobrepeso',
    },
    color: red.c90,
    stroke: {
      show: false,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoBaixoPeso(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('baixo'),
    })),
    label: {
      name: 'Baixo peso',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoAdequado(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('adequado'),
    })),
    label: {
      name: 'Adequado ou Eutrófico',
    },
    color: green.c90,
    stroke: {
      color: yellow.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaAdultoSobrepeso(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('sobrepeso'),
    })),
    label: {
      name: 'Sobrepeso',
    },
    color: yellow.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: yellow.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
    })),
    label: {
      name: 'Obesidade',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade1(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('obesidade1'),
    })),
    label: {
      name: 'Obesidade grau 1',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade2(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('obesidade2'),
    })),
    label: {
      name: 'Obesidade grau 2',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdultoObesidade3(xRange: DateRange): ReferenceArea<Moment> {
  return {
    area: getDomainPoints<Moment>(xRange).map((date) => ({
      x: date,
      upperLimit: getReferenceAreaUpperLimitAdulto('obesidade3'),
    })),
    label: {
      name: 'Obesidade grau 3',
    },
    color: red.c90,
    stroke: {
      show: false,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaCriancaMagrezaAcentuada(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-3),
    })),
    label: {
      name: 'Magreza acentuada',
    },
    color: gray.c90,
    stroke: {
      color: gray.c40,
    },
    tick: {
      color: gray.c40,
    },
  }
}

function getReferenceAreaCriancaMagreza(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: {
      name: 'Magreza',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaCriancaEutrofia(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(1),
    })),
    label: {
      name: 'Eutrófico',
    },
    color: green.c90,
    stroke: {
      color: yellow.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaCriancaSobrepeso(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    label: {
      name: idadeNoRange.years() <= 5 ? 'Risco de sobrepeso' : 'Sobrepeso',
    },
    color: yellow.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: yellow.c60,
    },
  }
}

function getReferenceAreaCriancaObesidade(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(3),
    })),
    label: {
      name: idadeNoRange.years() <= 5 ? 'Sobrepeso' : 'Obesidade',
    },
    color: red.c90,
    stroke: {
      color: gray.c40,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaCriancaObesidadeGrave(
  getsPercentis: ReferenceAreasCalculator[],
  idadeNoRange: Duration
): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    label: {
      name: idadeNoRange.years() <= 5 ? 'Obesidade' : 'Obesidade grave',
    },
    color: gray.c90,
    stroke: {
      show: false,
    },
    tick: {
      color: gray.c40,
    },
  }
}

const getReferenceAreaUpperLimitAdulto = (type: keyof ParametrosImcAdulto) => (imcAdulto[type] as ImcRange)?.maximo

const getReferenceAreaUpperLimitIdoso = (type: keyof ParametrosImcIdosoEGestante) =>
  (imcIdoso[type] as ImcRange)?.maximo

const styles = {
  alertImcGestante: {
    marginBottom: '1rem',
  },
  iconAlertImcGestante: {
    svg: {
      fontSize: '1rem',
    },
  },
}
